export const MAX_CACHED_QUANTITY_LOCATIONS = 10;

export interface PruneCachedQuantityLocationsInput {
	lastUpdateByLocation: Record<string, string>;
	maxLocations?: number;
}

export interface QuantitySnapshotSource {
	nodes?: ReadonlyArray<{ materialItemId: string; quantity: number }> | null;
	totalCount?: number | null;
}

export interface QuantitySnapshot {
	quantityByItemId: Map<string, number>;
	totalCount: number | null;
}

export const buildQuantitySnapshot = (
	source: QuantitySnapshotSource | null | undefined
): QuantitySnapshot | undefined => {
	if (source === null || source === undefined) return undefined;
	const quantityByItemId = new Map<string, number>();
	for (const node of source.nodes ?? []) {
		quantityByItemId.set(node.materialItemId, node.quantity);
	}
	return { quantityByItemId, totalCount: typeof source.totalCount === "number" ? source.totalCount : null };
};

export const pruneCachedQuantityLocations = ({
	lastUpdateByLocation,
	maxLocations = MAX_CACHED_QUANTITY_LOCATIONS
}: PruneCachedQuantityLocationsInput): string[] => {
	const locationIds = Object.keys(lastUpdateByLocation);
	if (locationIds.length <= maxLocations) return [];

	const oldestFirst = [...locationIds].sort((left, right) => {
		const leftAt = Date.parse(lastUpdateByLocation[left] ?? "");
		const rightAt = Date.parse(lastUpdateByLocation[right] ?? "");
		const leftValue = Number.isFinite(leftAt) ? leftAt : 0;
		const rightValue = Number.isFinite(rightAt) ? rightAt : 0;
		if (leftValue === rightValue) return left < right ? -1 : 1;
		return leftValue - rightValue;
	});

	return oldestFirst.slice(0, locationIds.length - maxLocations);
};

const hasQuantitySnapshotChanged = (
	before: QuantitySnapshot | undefined,
	after: QuantitySnapshot | undefined
): boolean => {
	if (before === undefined || after === undefined) return false;
	const isTotalCountComparable = before.totalCount !== null && after.totalCount !== null;
	if (isTotalCountComparable && before.totalCount !== after.totalCount) return true;
	if (before.quantityByItemId.size !== after.quantityByItemId.size) return true;
	for (const [materialItemId, quantity] of before.quantityByItemId) {
		if (!after.quantityByItemId.has(materialItemId)) return true;
		if (after.quantityByItemId.get(materialItemId) !== quantity) return true;
	}
	return false;
};

export default hasQuantitySnapshotChanged;
